import { Link } from 'react-router-dom';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import FilamentComparePanel from '../../../components/feature/FilamentComparePanel';
import { FilamentStressStrainChart } from '../../../components/feature/FilamentStressStrainChart';
import { useSEO } from '../../../hooks/useSEO';
import { useFilamentText } from '../../../hooks/useFilamentText';
import { useLocation } from 'react-router-dom';
import BendingStrengthCard from '../../../components/feature/BendingStrengthCard';

const TPUPage = () => {
  const location = useLocation();
  const isEnglish = location.pathname === '/en' || location.pathname.startsWith('/en/');
  const TEXT = useFilamentText();
  const materialData = TEXT.materials?.['tpu'];

  useSEO({
    title: 'TPU',
    description: isEnglish
      ? 'Highly flexible and elastic TPU filament. Excellent wear resistance and rubber-like texture. Ideal for phone cases, gaskets, and shoe soles.'
      : '高い柔軟性と弾性を持つTPUフィラメント。優れた耐摩耗性とゴムライクな質感。スマホケースやガスケット、靴底などに最適。',
    keywords: '3Dプリンター,TPU,フィラメント,柔軟性,弾性,ゴム',
    canonicalPath: isEnglish ? '/en/filament/tpu' : '/filament/tpu'
  });

  const breadcrumbItems = isEnglish ? [
    { label: 'Home', path: '/en' },
    { label: 'Filament Data', path: '/filament' },
    { label: 'TPU', path: '/en/filament/tpu' }
  ] : [
    { label: 'ホーム', path: '/' },
    { label: 'フィラメントデータ', path: '/filament' },
    { label: 'TPU', path: '/filament/tpu' }
  ];

  const filamentId = 'tpu';

  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="space-y-8 mt-8">
            {/* タイトル */}
            <div className="text-center">
              <h1 className="text-4xl font-bold text-white font-['Exo'] mb-4">
                TPU
              </h1>
              <div className="section-divider max-w-md mx-auto"></div>
            </div>

            {/* 比較指標 */}
            <FilamentComparePanel slug="tpu" />

            {/* 特徴 */}
            <div className="metal-panel p-8 border-l-4 border-accent">
              <h2 className="text-3xl font-semibold text-white font-['Exo'] mb-6 accent-line pb-3">
                {TEXT.features}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ul className="text-metal-light space-y-3 text-lg">
                  {materialData?.features.slice(0, 3).map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <i className="ri-check-line text-green-400 mt-1"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <ul className="text-metal-light space-y-3 text-lg">
                  {materialData?.features.slice(3).map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <i className="ri-check-line text-green-400 mt-1"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 基本情報 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.basicInfo}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.classification}</span>
                      <span className="difficulty-intermediate px-3 py-1 rounded text-sm font-semibold">{TEXT.intermediate}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.baseResin}</span>
                      <span className="text-white">{materialData?.baseResin}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 推奨設定 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.recommendedSettings}
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#3a3d42] text-white">
                      <th className="px-4 py-3 text-left">{TEXT.item}</th>
                      <th className="px-4 py-3 text-left">{TEXT.recommendedValue}</th>
                      <th className="px-4 py-3 text-left">{TEXT.notes}</th>
                    </tr>
                  </thead>
                  <tbody className="text-metal-light">
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.nozzleTemp}</td>
                      <td className="px-4 py-3 text-white">210-230°C</td>
                      <td className="px-4 py-3">{isEnglish ? 'Adjust by hardness' : '硬度により調整'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.bedTemp}</td>
                      <td className="px-4 py-3 text-white">40-60°C</td>
                      <td className="px-4 py-3">{isEnglish ? 'Improves adhesion' : '密着性向上'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.fanSetting}</td>
                      <td className="px-4 py-3 text-white">50-100%</td>
                      <td className="px-4 py-3">{isEnglish ? 'Overhangs only' : 'オーバーハング部のみ'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.printSpeed}</td>
                      <td className="px-4 py-3 text-white">15-30mm/s</td>
                      <td className="px-4 py-3">{isEnglish ? 'Slow speed required' : '低速必須'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.nozzleDiameter}</td>
                      <td className="px-4 py-3 text-white">0.4-0.6mm</td>
                      <td className="px-4 py-3">{isEnglish ? 'Larger diameter more stable' : '大径で安定'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{isEnglish ? 'Retraction' : 'リトラクション'}</td>
                      <td className="px-4 py-3 text-white">1-3mm</td>
                      <td className="px-4 py-3">{isEnglish ? 'Minimize' : '最小限に'}</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3">{TEXT.caution}</td>
                      <td className="px-4 py-3 text-white" colSpan={2}>{isEnglish ? 'Direct drive recommended, slow speed' : 'ダイレクトドライブ推奨、低速印刷'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* 応力-ひずみグラフ（CSV自動探索） */}
            {/* 曲げ強度 */}
            <BendingStrengthCard slug={filamentId} isEnglish={isEnglish} />

            <FilamentStressStrainChart 
              filamentId={filamentId}
              filamentName="TPU"
            />

            {/* 派生・バリエーション */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.derivatives}
              </h2>
              <p className="text-metal-light mb-6">
                {materialData?.derivatives}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-metal p-4 rounded">
                  <h3 className="text-white font-semibold mb-2">TPU 85A</h3>
                  <p className="text-metal-light text-sm">{isEnglish ? 'Standard hardness. Balanced flexibility and printability.' : '標準的な硬度。バランスの取れた柔軟性と印刷性。'}</p>
                </div>
                <div className="bg-metal p-4 rounded">
                  <h3 className="text-white font-semibold mb-2">TPU 95A</h3>
                  <p className="text-metal-light text-sm">{isEnglish ? 'Slightly harder. Easier to print with good mechanical strength.' : 'やや硬め。印刷しやすく、機械的強度も確保。'}</p>
                </div>
                <div className="bg-metal p-4 rounded">
                  <h3 className="text-white font-semibold mb-2">TPU 70A</h3>
                  <p className="text-metal-light text-sm">{isEnglish ? 'Very flexible. Rubber-like texture, high print difficulty.' : '非常に柔軟。ゴムに近い質感、印刷難易度高い。'}</p>
                </div>
                <div className="bg-metal p-4 rounded">
                  <h3 className="text-white font-semibold mb-2">TPU-CF</h3>
                  <p className="text-metal-light text-sm">{isEnglish ? 'Carbon fiber reinforced. Maintains flexibility with improved strength.' : 'カーボン繊維入り。柔軟性を保ちつつ強度向上。'}</p>
                </div>
              </div>
            </div>

            {/* 使用例 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.useCases}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-white font-semibold mb-2">{isEnglish ? 'Suitable Applications' : '向いている造形'}</h3>
                  <ul className="text-metal-light space-y-1 text-sm">
                    {materialData?.suitableUses.map((use, idx) => (
                      <li key={idx}>• {use}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">{isEnglish ? 'Pros & Cons' : '長所・短所'}</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-green-400">{isEnglish ? 'Pros:' : '長所：'}</span>
                      <span className="text-metal-light"> {materialData?.proscons.pros}</span>
                    </div>
                    <div>
                      <span className="text-red-400">{isEnglish ? 'Cons:' : '短所：'}</span>
                      <span className="text-metal-light"> {materialData?.proscons.cons}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 注意事項 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.precautions}
              </h2>
              <div className="text-metal-light space-y-2">
                {materialData?.precautions.map((precaution, idx) => (
                  <p key={idx}>• {precaution}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default TPUPage;
